/**
 * Упоминания пользователя (@username его сотрудника) — бейдж в шапке.
 * Опрос раз в 30 с (и сразу при возврате на вкладку). Упоминание живёт windowMin минут, потом пропадает;
 * скрытые вручную не показываются. Окно и скрытые — в этом браузере.
 */
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import type { MentionDto } from '@sync-tracker/shared';
import { api } from '@/api';
import { useNow } from '@/lib/now';

const WINDOW_KEY = 'st-mentions-window';
const DISMISSED_KEY = 'st-mentions-dismissed';
export const MENTION_WINDOWS = [5, 10, 15, 30, 60] as const;
const POLL_MS = 30_000;

const read = <T>(key: string, fallback: T): T => {
  try {
    const v = localStorage.getItem(key);
    return v == null ? fallback : (JSON.parse(v) as T);
  } catch {
    return fallback;
  }
};
const write = (key: string, v: unknown) => {
  try {
    localStorage.setItem(key, JSON.stringify(v));
  } catch {
    /* приватный режим */
  }
};
export const mentionKey = (m: Pick<MentionDto, 'chatId' | 'messageId'>) => `${m.chatId}:${m.messageId}`;

export const useMentions = defineStore('mentions', () => {
  const now = useNow();
  const username = ref<string | null>(null);
  const items = ref<MentionDto[]>([]);
  const loaded = ref(false);
  const windowMin = ref<number>(clamp(Number(read(WINDOW_KEY, 15))));
  /** Скрытые вручную: ключ → когда скрыли (чистим то, что уже вышло из окна). */
  const dismissed = ref<Record<string, number>>(read(DISMISSED_KEY, {}));

  function clamp(v: number) {
    return Number.isFinite(v) ? Math.max(1, Math.min(24 * 60, Math.round(v))) : 15;
  }

  async function refresh() {
    try {
      const r = await api.tickets.mentions(windowMin.value);
      username.value = r.username;
      items.value = r.items;
      loaded.value = true;
    } catch {
      /* сеть моргнула — покажем старое */
    }
  }

  const visible = computed(() => {
    const since = now.value - windowMin.value * 60_000;
    return items.value.filter((m) => new Date(m.date).getTime() > since && !dismissed.value[mentionKey(m)]);
  });

  function setWindow(min: number) {
    windowMin.value = clamp(min);
    write(WINDOW_KEY, windowMin.value);
    void refresh();
  }
  function dismiss(m: MentionDto) {
    dismissed.value = { ...dismissed.value, [mentionKey(m)]: Date.now() };
    persistDismissed();
  }
  function dismissAll() {
    const next = { ...dismissed.value };
    for (const m of visible.value) next[mentionKey(m)] = Date.now();
    dismissed.value = next;
    persistDismissed();
  }
  function persistDismissed() {
    // храним сутки — дольше окно не бывает
    const cutoff = Date.now() - 24 * 3600_000;
    dismissed.value = Object.fromEntries(Object.entries(dismissed.value).filter(([, at]) => at > cutoff));
    write(DISMISSED_KEY, dismissed.value);
  }

  let timer: number | null = null;
  function start() {
    if (timer) return;
    void refresh();
    timer = window.setInterval(() => document.visibilityState === 'visible' && void refresh(), POLL_MS);
    document.addEventListener('visibilitychange', () => document.visibilityState === 'visible' && void refresh());
  }

  return { username, items, loaded, windowMin, visible, refresh, start, setWindow, dismiss, dismissAll };
});
